import { _ } from "../polyglot/polyglot.js";
import { getCSSVariable, pixelToValue } from "../helpers/css-util.js";
import { getResult } from "../helpers/functional-util.js";
import { widget } from "@foretagsplatsen/widgetjs";
import popoverWidget from "./popoverWidget/popoverWidget.js";

/**
 * Render an action button that triggers a popover on click. This
 * widget is only meant to be used in the action bar, on the
 * right-side of the screen.
 *
 * The widget takes care of disposing the popover, as well as closing it
 * when the popover action is executed.
 *
 * @param {string} spec.label - Label used in the button
 * @param {boolean | Function} [spec.isDisabled = false] - Whether or not the button
 *   can be clicked
 * @param {Renderable} [spec.popoverHeader] - Content of the popover header
 * @param {Renderable} spec.popoverContent - Main content of the popover
 * @param {Renderable} [spec.popoverFooter] - Replace content of the default popover footer
 * @param {function} [spec.popoverAction] - Primary action of the popover
 * @param {function} [spec.cancelAction] - Executed when canceling the changes
 * @param {string} [spec.cssClass] - Extra css class attached to the button
 * @param {string} [spec.dataTestName] - A string to find the button within the test framework
 * @param {string} [spec.dataHelpName] - String used by the help system to find the button
 * @param {string} [spec.popoverPrimaryButtonLabel] - The label of the popover's button
 * @param {string} [spec.popoverCssClass] - The css class attached to the popover
 * @param {string} [spec.buttonType] - CSS classes to specify what the
 *   button opening the popover will look like
 * @param {bool} [isNestedPopover] - True if this popover button is
 *   nested in another popover. This prevents the new popover to be
 *   appended below the first one
 */
export default widget.subclass((that, my) => {
	my.initialize = function ({
		label,
		isDisabled = false,
		popoverHeader,
		popoverContent,
		popoverFooter,
		popoverAction,
		cancelAction,
		cssClass = "",
		dataTestName = "",
		dataHelpName = "",
		popoverPrimaryButtonLabel = _("Apply"),
		popoverCssClass = "popover filter-content-picker-popover popover-report-settings",
		buttonType = "btn-default btn-block",
		isNestedPopover = false,
	}) {
		my.super(...arguments);

		my.label = label;
		my.isDisabled = isDisabled;

		my.popoverHeader = popoverHeader || my.label;
		my.popoverContent = popoverContent;
		my.popoverFooter = popoverFooter;
		my.popoverAction = popoverAction;
		my.cancelAction = cancelAction;

		my.cssClass = cssClass;
		my.dataTestName = dataTestName;
		my.dataHelpName = dataHelpName;
		my.popoverPrimaryButtonLabel = popoverPrimaryButtonLabel;
		my.popoverCssClass = popoverCssClass;
		my.buttonType = buttonType;
		my.isNestedPopover = isNestedPopover;
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);
		let primaryAction = null;
		if (my.popoverAction) {
			primaryAction = () => {
				my.popoverAction();
				that.popoverAction.trigger();
			};
		}

		my.popover = popoverWidget({
			placement: "left",
			offset: [-pixelToValue(getCSSVariable("--right-menu-padding")), 0],
			animation: false,
			header: my.popoverHeader,
			content: my.popoverContent,
			popoverCssClass: my.popoverCssClass,
			footer: my.popoverFooter,
			primaryLabel: my.popoverPrimaryButtonLabel,
			primaryAction,
			closeAction: my.cancelAction,
			closeLabel: my.cancelAction && _("Cancel"),
			showFooterDivider: false,
			viewport: my.isNestedPopover ? "#viewport" : "body",
		});
	};

	that.popoverAction = my.events.createEvent();

	that.hide = function () {
		my.popover.hide();
	};

	that.renderContentOn = function (_html) {
		// empty because all rendering is done in _renderRootOn().
	};

	my.renderRootOn = function (html) {
		const button = html.button(
			{
				id: that.getId(),
				klass: [
					"btn",
					my.buttonType,
					my.cssClass,
					{ disabled: getResult(my.isDisabled) },
				],
				"data-test": my.dataTestName,
				"data-help": my.dataHelpName,
			},
			my.label,
		);

		my.popover.setTriggerItem(button);

		return button;
	};
});
